import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Package, Mail, ExternalLink, Home } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import ArtworkUpload from "@/components/ArtworkUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Order confirmation shown after a successful Square Sandbox payment.
 * Reads the last order from sessionStorage (set by Checkout.jsx before redirect).
 */
export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [order, setOrder] = useState(null);
  const [artworkInfo, setArtworkInfo] = useState(null);

  useSEO({
    title: "Order confirmed",
    description: "Thank you for your order — Toronto Custom Signs & Wraps.",
    path: "/checkout/success",
  });

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("tcsw_last_order");
      if (raw) setOrder(JSON.parse(raw));
    } catch { /* noop */ }
  }, []);

  // Poll the public order status to know if artwork has been uploaded.
  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    axios.get(`${API}/checkout/orders/${orderId}/status`)
      .then(({ data }) => {
        if (!cancelled && data.artwork_uploaded) {
          setArtworkInfo({ filename: data.artwork_filename, size: data.artwork_size });
        }
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [orderId]);

  return (
    <div className="pt-28 pb-24 bg-ink-950 min-h-screen" data-testid="checkout-success-page">
      <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-gold/20 border border-gold flex items-center justify-center mb-6">
          <CheckCircle2 size={30} className="text-gold" />
        </div>
        <div className="eyebrow gold-bar mx-auto w-fit">Confirmed</div>
        <h1 className="font-display text-4xl md:text-5xl mt-4 tracking-tight" data-testid="success-title">
          Thank you for your order
        </h1>
        <p className="text-white/70 text-base mt-4 leading-relaxed">
          {(order?.artwork_attached || artworkInfo)
            ? "Your payment was successful and your artwork is attached. We'll email you when your order enters production."
            : "Your payment was successful and we've queued your job. You'll get a confirmation email shortly with a secure link to upload your artwork so we can start production."}
        </p>

        {order && (
          <div className="card-dark p-7 mt-10 text-left" data-testid="order-details">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-4">Order details</div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-[10px] uppercase tracking-[0.22em] text-white/40">Order ID</dt>
                <dd className="text-white/85 mt-1 font-mono text-xs break-all" data-testid="success-order-id">{order.order_id || orderId}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.22em] text-white/40">Amount charged</dt>
                <dd className="text-white/85 mt-1">${((order.total_cents || 0) / 100).toFixed(2)} {order.currency || "CAD"}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.22em] text-white/40">Payment</dt>
                <dd className="text-white/85 mt-1">
                  <span className="text-gold">{order.square_status || "COMPLETED"}</span>
                  {order.receipt_url && (
                    <>
                      {" · "}
                      <a href={order.receipt_url} target="_blank" rel="noreferrer" data-testid="square-receipt-link" className="text-gold hover:underline inline-flex items-center gap-1">
                        View receipt <ExternalLink size={11} />
                      </a>
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.22em] text-white/40">Next step</dt>
                <dd className="text-white/85 mt-1">
                  {(order?.artwork_attached || artworkInfo)
                    ? <span className="text-emerald-400">Artwork attached — nothing more to do</span>
                    : "Upload artwork (coming to your inbox)"}
                </dd>
              </div>
            </dl>
          </div>
        )}

        {orderId && (
          <div className="card-dark p-7 mt-8 text-left" data-testid="artwork-section">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold mb-3">Next step · Upload your artwork</div>
            <h2 className="font-display text-2xl text-white mb-3">Send us your print-ready file</h2>
            <p className="text-white/60 text-sm mb-5 leading-relaxed">
              Drop your PDF below to start production. If you&apos;d rather do this later, check your inbox — we&apos;ve emailed you a link to this same upload page.
            </p>
            <ArtworkUpload
              orderId={orderId}
              currentFile={artworkInfo}
              onUploaded={(data) => setArtworkInfo({ filename: data.filename, size: data.size })}
            />
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
          <StepCard icon={<Mail size={16} className="text-gold" />} title="Confirmation email" body="Check your inbox for the order confirmation and artwork upload link." />
          <StepCard icon={<Package size={16} className="text-gold" />} title="We print & ship" body="Once artwork is approved, we produce and ship your order Canada-wide." />
          <StepCard icon={<CheckCircle2 size={16} className="text-gold" />} title="Track progress" body="You'll receive shipping updates as your order moves through production." />
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/shop" data-testid="continue-shopping-success" className="btn-gold">
            Continue shopping
          </Link>
          <Link to="/" className="btn-ghost">
            <Home size={13} /> Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

function StepCard({ icon, title, body }) {
  return (
    <div className="card-dark p-5">
      <div className="mb-3">{icon}</div>
      <div className="font-display text-base text-white">{title}</div>
      <div className="text-white/50 text-xs mt-2 leading-relaxed">{body}</div>
    </div>
  );
}
